import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  ViewChild,
  inject,
} from '@angular/core';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { TimeUtils } from '@freelancer/time-utils';
import {
  FreelancerBreakpoints,
  FreelancerBreakpointValues,
} from '@freelancer/ui/breakpoints';
import { ButtonComponent } from '@freelancer/ui/button';
import { IconModule } from '@freelancer/ui/icon';
import { IconBackdrop, IconColor, IconSize } from '@freelancer/ui/icon';
import type { Observable } from 'rxjs';
import { Subject, Subscription } from 'rxjs';
import { repeat, startWith, takeUntil } from 'rxjs/operators';
import { trackByIndex } from '../helpers';
import { CarouselItemComponent } from './carousel-item.component';
import { CarouselNextButtonComponent } from './carousel-next-button.component';
import { CarouselPrevButtonComponent } from './carousel-prev-button.component';
import {
  CarouselAnimationSpeed,
  CarouselArrowPosition,
  CarouselDotIndicatorPosition,
  CarouselScrollBehaviour,
} from './carousel.types';

@Component({
  standalone: true,
  selector: 'fl-carousel',
  imports: [ButtonComponent, IconModule, NgTemplateOutlet],
  template: `
    <div
      class="Carousel"
      [attr.data-arrow-position]="arrowPosition"
      [attr.data-scroll-behaviour]="scrollBehaviour"
      (swipeleft)="handleNext()"
      (swiperight)="handlePrevious()"
    >
      @if (arrowPosition !== CarouselArrowPosition.OUTSIDE_BELOW) {
        <ng-container [ngTemplateOutlet]="leftArrow"></ng-container>
      }
      <div
        class="Carousel-list"
        #carouselList
        [attr.data-scroll-behaviour]="scrollBehaviour"
      >
        <div
          #carouselTrack
          class="Carousel-track"
          [class.IsAnimatable]="isAnimatable"
          [attr.data-active]="active"
          [attr.data-animation-speed]="animationSpeed"
        >
          <ng-content></ng-content>
        </div>
        @if (active) {
          @if (showDotIndicator && indicators.length > 1) {
            <div
              class="DotIndicator mb-mid"
              [attr.data-dot-indicator-position]="dotIndicatorPosition"
              [attr.data-arrow-position]="arrowPosition"
            >
              <div>
                @for (
                  item of indicators;
                  track trackByIndex(i);
                  let i = $index;
                  let last = $last
                ) {
                  <div
                    class="DotIndicator-item {{
                      last ? 'mr-none' : 'mr-xsmall'
                    }}"
                    [attr.aria-label]="'Dot indicator ' + i"
                    [class.IsActive]="slideIndex === i"
                    (click)="updateSlideIndex(i)"
                  ></div>
                }
              </div>
              @if (arrowPosition === CarouselArrowPosition.OUTSIDE_BELOW) {
                <div class="Carousel-arrowBelowContainer">
                  <ng-container [ngTemplateOutlet]="leftArrow"></ng-container>
                  <ng-container [ngTemplateOutlet]="rightArrow"></ng-container>
                </div>
              }
            </div>
          }
        }
      </div>
      @if (arrowPosition !== CarouselArrowPosition.OUTSIDE_BELOW) {
        <ng-container [ngTemplateOutlet]="rightArrow"></ng-container>
      }
    </div>

    <ng-template #leftArrow>
      @if (active) {
        <div
          class="Carousel-arrow Carousel-arrow--prev"
          [class.PrevGradient]="prevArrowBackdrop"
          [class.Carousel-arrow-dotIndicatorPosition]="
            dotIndicatorPosition === CarouselDotIndicatorPosition.OUTSIDE
          "
          [class.hide]="
            (arrowPosition !== CarouselArrowPosition.OUTSIDE_BELOW &&
              isFirstSlide &&
              !wrapAround) ||
            scrollBehaviour === CarouselScrollBehaviour.FREE
          "
        >
          <fl-button
            i18n-label="Carousel previous button"
            label="Previous"
            class="Carousel-arrow-button"
            [attr.data-can-scroll]="wrapAround || !isFirstSlide"
            [attr.data-default-arrow]="!carouselPrevButton"
            [class.mobile:hide]="hideArrows"
            [class.tablet:hide]="hideArrowsTablet"
            [class.desktop:hide]="hideArrowsDesktop"
            (click)="handlePrevious($event)"
          >
            @if (!carouselPrevButton) {
              <fl-icon
                class="Carousel-arrow-defaultIcon Carousel-arrow-defaultIcon--prev"
                [backdrop]="IconBackdrop.TRANSPARENT"
                [backdropSizing]="'standard'"
                [name]="'ui-arrow-back'"
                [color]="IconColor.LIGHT"
                [size]="arrowSize"
                [sizeTablet]="arrowSizeTablet"
                [sizeDesktop]="arrowSizeDesktop"
                [useIconFont]="true"
              ></fl-icon>
            }
            <ng-content select="fl-carousel-prev-btn"></ng-content>
          </fl-button>
        </div>
      }
    </ng-template>

    <ng-template #rightArrow>
      @if (active) {
        <div
          class="Carousel-arrow Carousel-arrow--next"
          [class.NextGradient]="nextArrowBackdrop"
          [class.Carousel-arrow-dotIndicatorPosition]="
            dotIndicatorPosition === CarouselDotIndicatorPosition.OUTSIDE
          "
          [class.hide]="
            (arrowPosition !== CarouselArrowPosition.OUTSIDE_BELOW &&
              isLastSlide &&
              !wrapAround) ||
            scrollBehaviour === CarouselScrollBehaviour.FREE
          "
        >
          <fl-button
            i18n-label="Carousel next button"
            label="Next"
            class="Carousel-arrow-button"
            [attr.data-can-scroll]="wrapAround || !isLastSlide"
            [attr.data-default-arrow]="!carouselNextButton"
            [class.mobile:hide]="hideArrows"
            [class.tablet:hide]="hideArrowsTablet"
            [class.desktop:hide]="hideArrowsDesktop"
            (click)="handleNext($event)"
          >
            @if (!carouselNextButton) {
              <fl-icon
                class="Carousel-arrow-defaultIcon Carousel-arrow-defaultIcon--next"
                [backdrop]="IconBackdrop.TRANSPARENT"
                [backdropSizing]="'standard'"
                [name]="'ui-arrow-forward'"
                [color]="IconColor.LIGHT"
                [size]="arrowSize"
                [sizeTablet]="arrowSizeTablet"
                [sizeDesktop]="arrowSizeDesktop"
                [useIconFont]="true"
              ></fl-icon>
            }
            <ng-content select="fl-carousel-next-btn"></ng-content>
          </fl-button>
        </div>
      }
    </ng-template>
  `,
  styleUrls: ['./carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselComponent implements OnChanges, AfterViewInit, OnDestroy {
  private breakpointObserver = inject(BreakpointObserver);
  private renderer = inject(Renderer2);
  private changeDetector = inject(ChangeDetectorRef);
  private timeUtils = inject(TimeUtils);
  private platformId = inject(PLATFORM_ID);

  CarouselArrowPosition = CarouselArrowPosition;
  CarouselDotIndicatorPosition = CarouselDotIndicatorPosition;
  CarouselScrollBehaviour = CarouselScrollBehaviour;
  IconBackdrop = IconBackdrop;
  IconColor = IconColor;
  trackByIndex = trackByIndex;

  trackWidth: number;
  slideIndex: number;
  currentGroupIndex = 0;
  isFirstSlide = false;
  isLastSlide = false;
  maxImageCount = 0;
  isAnimatable = false;
  isMobile = false;
  hideArrows: boolean;
  hideArrowsTablet: boolean;
  hideArrowsDesktop: boolean;
  slidesCount = 1;
  private screenSizeSubscription?: Subscription;
  @RepetitiveSubscription()
  private carouselItemsSubscription?: Subscription;
  @RepetitiveSubscription()
  private autoplaySubscription?: Subscription;
  autoplayTimer$: Observable<number>;
  indicators: readonly null[] = [];

  private stopAutoplay$ = new Subject<void>();
  private startAutoplay$ = new Subject<void>();

  // FIXME: T300116 Enable hydration. Currently has mismatched content due to mobile "responsiveness"
  @HostBinding('attr.ngSkipHydration') skipHydration = 'true';

  @Input() animationSpeed: CarouselAnimationSpeed = 'medium';
  @Input() arrows = true;
  @Input() arrowsTablet?: boolean;
  @Input() arrowsDesktop?: boolean;
  @Input() arrowSize: IconSize = IconSize.MID;
  @Input() arrowSizeTablet?: IconSize;
  @Input() arrowSizeDesktop?: IconSize;
  /** Sets an offset in px for the arrow position in string form */
  @HostBinding('style.--arrow-offset')
  @Input()
  arrowOffset?: string;
  @HostBinding('style.--arrow-offset-tablet')
  @Input()
  arrowOffsetTablet?: string;
  @HostBinding('style.--arrow-offset-desktop')
  @Input()
  arrowOffsetDesktop?: string;
  @HostBinding('style.--carousel-item-width')
  get carouselItemWidth(): string {
    return `${100 / this.slidesToShow}%`;
  }
  @HostBinding('style.--carousel-item-width-tablet')
  get carouselItemWidthTablet(): string {
    return `${100 / (this.slidesToShowTablet || this.slidesToShow)}%`;
  }
  @HostBinding('style.--carousel-item-width-desktop')
  get carouselItemWidthDesktop(): string {
    return `${
      100 /
      (this.slidesToShowDesktop || this.slidesToShowTablet || this.slidesToShow)
    }%`;
  }
  @HostBinding('style.--carousel-item-width-desktop-large')
  get carouselItemWidthDesktopLarge(): string {
    return `${
      100 /
      (this.slidesToShowDesktopLarge ||
        this.slidesToShowDesktop ||
        this.slidesToShowTablet ||
        this.slidesToShow)
    }%`;
  }
  @HostBinding('style.--carousel-item-width-desktop-xlarge')
  get carouselItemWidthDesktopXLarge(): string {
    return `${
      100 /
      (this.slidesToShowDesktopXLarge ||
        this.slidesToShowDesktopLarge ||
        this.slidesToShowDesktop ||
        this.slidesToShowTablet ||
        this.slidesToShow)
    }%`;
  }
  @HostBinding('style.--carousel-item-width-desktop-xxlarge')
  get carouselItemWidthDesktopXXLarge(): string {
    return `${
      100 /
      (this.slidesToShowDesktopXXLarge ||
        this.slidesToShowDesktopXLarge ||
        this.slidesToShowDesktopLarge ||
        this.slidesToShowDesktop ||
        this.slidesToShowTablet ||
        this.slidesToShow)
    }%`;
  }
  /** Adds a dark gradient under the previous arrow. Used for carousels with light backgrounds */
  @Input() prevArrowBackdrop = false;
  /** Adds a dark gradient under the next arrow. Used for carousels with light backgrounds */
  @Input() nextArrowBackdrop = false;
  @Input() currentIndex = 0;
  @Input() slidesToShow = 1;
  @Input() slidesToShowTablet?: number;
  @Input() slidesToShowDesktop?: number;
  @Input() slidesToShowDesktopLarge?: number;
  @Input() slidesToShowDesktopXLarge?: number;
  @Input() slidesToShowDesktopXXLarge?: number;

  /**
   * Determines if a portion of the next slide should be visible.
   * This is applied only when CarouselScrollBehaviour is FREE or FREE_AND_INDIVIDUAL.
   * If true, a part of the nth slide (where n is the number of slides to show) will be visible.
   */
  @Input() showPeekSlide = false;
  /**
   * Defines the width of the peeking slide as a fraction of the slide width.
   * Values should be between 0 to 1. By default, 50% (0.5) of the peeking slide is visible.
   */
  @Input() peekSlideWidth = 0.5;
  /**
   * Defines if scroll behaviour will use indices or not.
   * This is overridden and set to true when behaviour is FREE_AND_INDIVIDUAL.
   */
  @Input() noIndex = false;
  @Input() padding = 0;
  @Input() paddingTablet?: number;
  @Input() paddingDesktop?: number;
  @Input() showDotIndicator = false;
  @Input() autoplay = false;
  @Input() pauseAutoplayOnHover = false;
  @Input() autoplayDuration = 10_000;
  @Input() useAnimation = true;
  /** Defines the position of the arrows. OUTSIDE_BELOW only applies with showDotIndicator + OUTSIDE.
   * EDGE centres each arrow half-outside the box - use where a fully outside arrow would be clipped. */
  @Input() arrowPosition: CarouselArrowPosition = CarouselArrowPosition.INSIDE;
  @Input()
  @Input()
  dotIndicatorPosition: CarouselDotIndicatorPosition =
    CarouselDotIndicatorPosition.INSIDE;
  @Input() scrollBehaviour: CarouselScrollBehaviour =
    CarouselScrollBehaviour.GROUP;
  @HostBinding('attr.data-scroll-behaviour')
  get hostScrollBehaviour(): CarouselScrollBehaviour {
    return this.scrollBehaviour;
  }
  @Input() active = true;
  /**
   * Determines if the carousel wraps around from the last slide to first (and vice versa) when clicking the next/previous arrows
   * NOTE: This does not work with CarouselScrollBehaviour.FREE
   */
  @Input() wrapAround = false;

  @Output() slideChange = new EventEmitter<number>();

  @ViewChild('carouselTrack', { read: ElementRef })
  carouselTrack: ElementRef;
  @ViewChild('carouselList', { read: ElementRef })
  carouselList: ElementRef;

  @ContentChildren(CarouselItemComponent)
  carouselItems: QueryList<CarouselItemComponent>;
  @ContentChild(CarouselPrevButtonComponent)
  carouselPrevButton: CarouselPrevButtonComponent;
  @ContentChild(CarouselNextButtonComponent)
  carouselNextButton: CarouselNextButtonComponent;

  ngOnChanges(changes: SimpleChanges): void {
    if ('active' in changes) {
      if (this.active) {
        this.activate();
      } else {
        this.deactivate();
      }
    }

    if ('scrollBehaviour' in changes) {
      // This scroll behaviour only works with no index individual scrolling for now
      if (
        this.scrollBehaviour === CarouselScrollBehaviour.FREE_AND_INDIVIDUAL
      ) {
        this.noIndex = true;
      }
    }

    if ('currentIndex' in changes && this.carouselItems) {
      this.setSlideIndex();
      if (this.isFreeScroll) {
        this.scrollTo(this.currentIndex);
      } else {
        this.setActiveSlide();
        this.moveTrackPosition();
      }
    }

    if (
      ('padding' in changes ||
        'paddingTablet' in changes ||
        'paddingDesktop' in changes) &&
      this.carouselItems
    ) {
      this.setSpacing();
    }

    if (
      ('slidesToShow' in changes ||
        'slidesToShowTablet' in changes ||
        'slidesToShowDesktop' in changes ||
        'slidesToShowDesktopLarge' in changes ||
        'slidesToShowDesktopXLarge' in changes ||
        'slidesToShowDesktopXXLarge' in changes) &&
      this.carouselItems
    ) {
      this.updateSlidesToShow();
    }

    if (
      'arrows' in changes ||
      'arrowsTablet' in changes ||
      'arrowsDesktop' in changes
    ) {
      this.hideArrows = !this.arrows;
      this.hideArrowsTablet =
        (this.hideArrows && this.arrowsTablet === undefined) ||
        this.arrowsTablet === false;
      this.hideArrowsDesktop =
        (this.hideArrowsTablet && this.arrowsDesktop === undefined) ||
        this.arrowsDesktop === false;
    }

    if ('useAnimation' in changes) {
      if (this.useAnimation) {
        this.setTrackAnimation();
      } else {
        this.isAnimatable = false;
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.active) {
      this.activate();
    }

    if (isPlatformBrowser(this.platformId)) {
      this.screenSizeSubscription = this.breakpointObserver
        .observe([
          FreelancerBreakpoints.MOBILE,
          FreelancerBreakpoints.TABLET,
          FreelancerBreakpoints.DESKTOP_SMALL,
          FreelancerBreakpoints.DESKTOP_LARGE,
          FreelancerBreakpoints.DESKTOP_XLARGE,
          FreelancerBreakpoints.DESKTOP_XXLARGE,
        ])
        .subscribe(_ => {
          this.updateSlidesToShow();
          this.setSpacing();
          this.changeDetector.markForCheck();
        });
    }
  }

  @HostListener('mouseenter')
  handleMouseEnter(): void {
    if (this.autoplay && this.pauseAutoplayOnHover) {
      this.stopAutoplay$.next();
    }
  }

  @HostListener('mouseleave')
  handleMouseLeave(): void {
    if (this.autoplay && this.pauseAutoplayOnHover) {
      this.startAutoplay$.next();
    }
  }

  isDesktopXXLarge(): boolean {
    return this.breakpointObserver.isMatched(
      FreelancerBreakpoints.DESKTOP_XXLARGE,
    );
  }

  isDesktopXLarge(): boolean {
    return this.breakpointObserver.isMatched(
      FreelancerBreakpoints.DESKTOP_XLARGE,
    );
  }

  isDesktopLarge(): boolean {
    return this.breakpointObserver.isMatched(
      FreelancerBreakpoints.DESKTOP_LARGE,
    );
  }

  isDesktop(): boolean {
    return this.breakpointObserver.isMatched(
      FreelancerBreakpoints.DESKTOP_SMALL,
    );
  }

  isTablet(): boolean {
    return this.breakpointObserver.isMatched(FreelancerBreakpoints.TABLET);
  }

  updateSlidesToShow(): void {
    this.slidesCount = !isPlatformBrowser(this.platformId)
      ? this.slidesToShow
      : this.isDesktopXXLarge() && this.slidesToShowDesktopXXLarge
        ? this.slidesToShowDesktopXXLarge
        : this.isDesktopXLarge() && this.slidesToShowDesktopXLarge
          ? this.slidesToShowDesktopXLarge
          : this.isDesktopLarge() && this.slidesToShowDesktopLarge
            ? this.slidesToShowDesktopLarge
            : this.isDesktop() && this.slidesToShowDesktop
              ? this.slidesToShowDesktop
              : this.isTablet() && this.slidesToShowTablet
                ? this.slidesToShowTablet
                : this.slidesToShow;

    this.setViewWidths();
    this.moveTrackPosition();
    this.generateIndicators();
  }

  activate(): void {
    // whenever the carousel items change, reset the track
    this.carouselItemsSubscription = this.carouselItems.changes
      .pipe(startWith(this.carouselItems))
      .subscribe(() => {
        this.setSlideIndex();
        this.setViewWidths();
        this.setSpacing();
        this.updateSlidesToShow();
        this.setActiveSlide();
        this.moveTrackPosition();
        this.generateIndicators();
        if (this.useAnimation) {
          this.setTrackAnimation();
        }
        // T245016: Keeping as detectChanges() instead of markForCheck() to allow testing library to continue functioning per usual.
        // eslint-disable-next-line local-rules/no-detectchanges-without-detach-reattach
        this.changeDetector.detectChanges();
      });

    if (this.autoplay && this.autoplayDuration) {
      // Autoplay to next carousel item every 10 seconds and reset the timer
      // everytime the user manually clicks next/previous
      this.autoplayTimer$ = this.timeUtils
        .rxInterval(this.autoplayDuration)
        .pipe(takeUntil(this.stopAutoplay$))
        .pipe(repeat({ delay: () => this.startAutoplay$ }));

      this.autoplaySubscription = this.autoplayTimer$.subscribe(() => {
        this.handleAutoplaySlide();
        this.changeDetector.markForCheck();
      });
    }

    if (isPlatformBrowser(this.platformId)) {
      // check if browser is smaller than tablet
      this.isMobile =
        window.innerWidth < parseInt(FreelancerBreakpointValues.TABLET, 10);
      this.setViewWidths();
    }

    this.generateIndicators();
  }

  deactivate(): void {
    // cleanup track styles
    this.renderer.removeStyle(this.carouselTrack.nativeElement, 'width');
    this.renderer.removeStyle(this.carouselTrack.nativeElement, 'transform');

    // cleanup carousel item styles
    this.carouselItems.forEach((carouselItem: CarouselItemComponent) => {
      this.renderer.removeStyle(carouselItem.elementRef.nativeElement, 'width');
    });

    this.carouselItemsSubscription?.unsubscribe();
    this.autoplaySubscription?.unsubscribe();
  }

  generateIndicators(): void {
    if (this.showDotIndicator) {
      this.changeDetector.detach();
      const itemCount = this.isIndividualScroll
        ? this.carouselItems.length
        : this.getTotalGroupCount() + 1;

      this.indicators = [...new Array(itemCount)];

      // reattach and detect changes;
      this.changeDetector.reattach();
      this.changeDetector.detectChanges();
    }
  }

  private get isIndividualScroll(): boolean {
    return this.scrollBehaviour === CarouselScrollBehaviour.INDIVIDUAL;
  }

  private get isGroupScroll(): boolean {
    return this.scrollBehaviour === CarouselScrollBehaviour.GROUP;
  }

  private get isFreeScroll(): boolean {
    return this.scrollBehaviour === CarouselScrollBehaviour.FREE;
  }

  private get isFreeAndIndividualScroll(): boolean {
    return this.scrollBehaviour === CarouselScrollBehaviour.FREE_AND_INDIVIDUAL;
  }

  // Prevents unnecessary animation on load
  setTrackAnimation(): void {
    this.timeUtils.setTimeout(() => {
      this.isAnimatable = true;
    }, 250);
  }

  setSlideIndex(): void {
    this.maxImageCount = this.carouselItems.length - 1;
    this.updateSlideIndex(this.currentIndex);
  }

  setMaxImageCount(): void {
    this.maxImageCount = this.carouselItems.length - 1;
  }

  setViewWidths(): void {
    this.setTrackWidth();
    this.setCarouselItemWidth(this.carouselItems.length);
  }

  setTrackWidth(): void {
    if (this.isIndividualScroll) {
      this.renderer.removeStyle(this.carouselTrack.nativeElement, 'width');
      return;
    }

    // Calculate the hidden portion of the peeking slide, if applicable
    if (this.peekSlideWidth < 0 || this.peekSlideWidth > 1) {
      throw new Error(
        'The value of peekSlideWidth must be between 0 and 1, inclusive',
      );
    }

    const peekingSlideHiddenWidth =
      (this.isFreeScroll || this.isFreeAndIndividualScroll) &&
      (this.isMobile || this.showPeekSlide)
        ? 1 - this.peekSlideWidth
        : 0;

    // Calculate the number of slides to show at once, adjusted for the peeking slide
    const slidesToShowAtOnce = this.slidesCount - peekingSlideHiddenWidth;

    // Calculate the track width as a percentage
    this.trackWidth = (this.carouselItems.length / slidesToShowAtOnce) * 100;

    // Set the track width
    this.renderer.setStyle(
      this.carouselTrack.nativeElement,
      'width',
      `${this.trackWidth}%`,
    );
  }

  setCarouselItemWidth(slideCount: number): void {
    // Individual item widths are CSS-owned; clear leftover inline widths.
    if (this.isIndividualScroll) {
      this.carouselItems.forEach((carouselItem: CarouselItemComponent) => {
        this.renderer.removeStyle(
          carouselItem.elementRef.nativeElement,
          'width',
        );
      });
      return;
    }

    this.carouselItems.forEach((carouselItem: CarouselItemComponent) => {
      this.renderer.setStyle(
        carouselItem.elementRef.nativeElement,
        'width',
        `${this.trackWidth / (slideCount * (slideCount / this.slidesCount))}%`,
      );
    });
  }

  resetAutoplayTimer(): void {
    this.stopAutoplay$.next();
    this.startAutoplay$.next();
  }

  handleAutoplaySlide(): void {
    this.updateSlideIndex(this.isLastSlide ? 0 : this.slideIndex + 1);
  }

  handlePrevious(event?: MouseEvent): void {
    if (!this.active) {
      return;
    }

    if (event) {
      event.stopPropagation();
    }

    if (!this.isFirstSlide) {
      if (!this.isFreeScroll && !this.noIndex) {
        this.updateSlideIndex(this.slideIndex - 1);
      }

      if (this.isIndividualScroll || this.isFreeAndIndividualScroll) {
        this.scrollToNextItemInGroup('prev');
      }
    } else if (this.wrapAround) {
      if (!this.isFreeScroll && !this.noIndex) {
        const newSlideIndex = this.isIndividualScroll
          ? this.maxImageCount
          : this.getTotalGroupCount();
        this.updateSlideIndex(newSlideIndex);
      }

      if (this.isIndividualScroll || this.isFreeAndIndividualScroll) {
        this.scrollToNextItemInGroup('end');
      }
    }
  }

  handleNext(event?: MouseEvent): void {
    if (!this.active) {
      return;
    }

    if (event) {
      event.stopPropagation();
    }

    if (!this.isLastSlide) {
      if (!this.isFreeScroll && !this.noIndex) {
        this.updateSlideIndex(this.slideIndex + 1);
      }

      if (this.isIndividualScroll || this.isFreeAndIndividualScroll) {
        this.scrollToNextItemInGroup('next');
      }
    } else if (this.wrapAround) {
      if (!this.isFreeScroll && !this.noIndex) {
        this.updateSlideIndex(0);
      }

      if (this.isIndividualScroll || this.isFreeAndIndividualScroll) {
        this.scrollToNextItemInGroup('start');
      }
    }
  }

  updateSlideIndex(newSlideIndex: number): void {
    // Clamps slideIndex from 0 to carouselItems.length - 1 to prevent out of bounds
    this.slideIndex = Math.max(
      0,
      Math.min(this.carouselItems.length - 1, newSlideIndex),
    );
    this.moveTrackPosition();
    this.setActiveSlide();

    if (this.autoplay && this.autoplayDuration) {
      this.resetAutoplayTimer();
    }
  }

  setActiveSlide(): void {
    if (!this.isIndividualScroll || this.noIndex) {
      return;
    }

    this.carouselItems.forEach((carouselItem: CarouselItemComponent, i) => {
      const carouselElement = carouselItem.elementRef.nativeElement;

      if (this.slideIndex === i) {
        this.renderer.addClass(carouselElement, 'IsActive');
      } else {
        this.renderer.removeClass(carouselElement, 'IsActive');
      }
    });
  }

  setSpacing(): void {
    const padding = !isPlatformBrowser(this.platformId)
      ? this.padding
      : this.isDesktop() && this.paddingDesktop
        ? this.paddingDesktop
        : this.isTablet() && this.paddingTablet
          ? this.paddingTablet
          : this.padding;

    this.carouselItems.forEach((carouselItem: CarouselItemComponent) => {
      this.renderer.setStyle(
        carouselItem.elementRef.nativeElement,
        'padding',
        `0 ${padding}px`,
      );
    });

    this.renderer.setStyle(
      this.carouselList.nativeElement,
      'margin',
      `0 -${padding}px`,
    );
  }

  moveTrackPosition(): void {
    if (this.isIndividualScroll || this.isFreeAndIndividualScroll) {
      this.updateStates();
      return;
    }

    this.currentGroupIndex = this.slideIndex;
    this.renderer.setStyle(
      this.carouselTrack.nativeElement,
      'transform',
      `translateX(-${
        (this.currentGroupIndex / (this.trackWidth / 100)) * 100
      }%)`,
    );

    this.slideChange.emit(this.slideIndex);
    this.updateStates();
  }

  updateStates(): void {
    if (this.isFreeScroll) {
      this.isFirstSlide = true;
      this.isLastSlide = true;
    } else if (this.isGroupScroll) {
      this.isFirstSlide = this.slideIndex <= 0;
      this.isLastSlide = this.slideIndex >= this.getTotalGroupCount();
    } else if (this.isIndividualScroll || this.isFreeAndIndividualScroll) {
      const maxSlideIndex = this.carouselItems.length - 1;
      this.isFirstSlide = this.slideIndex <= 0;

      if (this.noIndex) {
        this.isLastSlide =
          this.slideIndex + this.slidesCount - 1 >= maxSlideIndex;
      } else {
        this.isLastSlide = this.slideIndex >= maxSlideIndex;
      }
    }
  }

  private getTotalGroupCount(): number {
    return Math.floor(this.maxImageCount / this.slidesCount);
  }

  ngOnDestroy(): void {
    this.carouselItemsSubscription?.unsubscribe();
    this.autoplaySubscription?.unsubscribe();
    this.screenSizeSubscription?.unsubscribe();
  }

  scrollTo(index: number): void {
    const firstItem = this.carouselItems.get(0);
    const itemWidth = Number(firstItem?.elementRef.nativeElement.offsetWidth);
    const containerWidth = Number(this.carouselList.nativeElement.offsetWidth);
    const containerLeftOffset = Number(
      this.carouselList.nativeElement.scrollLeft,
    );
    const leftOffset = itemWidth * index;

    // thresholds are 1/2 item from the viewports left and right side on mobile
    // thresholds are 1 item from the viewports left and right side on non mobile
    const thresholdOffset = this.isMobile ? 0.5 : 1;
    const leftThreshold = containerLeftOffset + itemWidth * thresholdOffset;
    const rightThreshold =
      containerLeftOffset + containerWidth - itemWidth * 0.5;

    // activate scroll if the right end of the item exceeds right threshold
    if (leftOffset + itemWidth >= rightThreshold) {
      this.carouselList.nativeElement.scroll({
        top: 0,
        left: leftOffset - itemWidth * 2,
        behavior: 'smooth',
      });
    } else if (leftOffset < leftThreshold) {
      this.carouselList.nativeElement.scroll({
        top: 0,
        left: leftOffset - itemWidth * thresholdOffset,
        behavior: 'smooth',
      });
    }

    this.slideChange.emit(this.slideIndex);
  }

  scrollToNextItemInGroup(
    scrollDirection: 'next' | 'prev' | 'start' | 'end',
  ): void {
    const firstItem = this.carouselItems.get(0);
    const itemWidth = Number(firstItem?.elementRef.nativeElement.offsetWidth);
    const containerWidth = Number(this.carouselList.nativeElement.scrollWidth);
    const containerLeftOffset = Number(
      this.carouselList.nativeElement.scrollLeft,
    );
    const visibleWidth = Number(this.carouselList.nativeElement.clientWidth); // Visible width of the container

    let newLeftPos: number;

    switch (scrollDirection) {
      case 'prev':
        newLeftPos = containerLeftOffset - itemWidth;
        break;
      case 'next':
        newLeftPos = containerLeftOffset + itemWidth;
        break;
      case 'start':
        newLeftPos = 0;
        break;
      case 'end':
        newLeftPos = containerWidth - visibleWidth;
        break;
      default:
        console.error(`Invalid scroll direction ${scrollDirection}`);
        return;
    }

    const newLeftPosFloor = Math.floor(newLeftPos / itemWidth) * itemWidth;
    const newLeftPosCeil = Math.ceil(newLeftPos / itemWidth) * itemWidth;

    newLeftPos =
      Math.abs(newLeftPosFloor - newLeftPos) <
      Math.abs(newLeftPosCeil - newLeftPos)
        ? newLeftPosFloor
        : newLeftPosCeil;

    // Ensure newLeftPos doesn't exceed the container boundaries
    newLeftPos = Math.max(
      0,
      Math.min(newLeftPos, containerWidth - visibleWidth),
    );

    this.carouselList.nativeElement.scroll({
      top: 0,
      left: newLeftPos,
      behavior: this.useAnimation ? 'smooth' : 'auto',
    });

    this.isFirstSlide = newLeftPos === 0;
    this.isLastSlide = newLeftPos + visibleWidth >= containerWidth;

    // updateStates should only update the isFirstSlide and isLastSlide states when noIndex=false
    // scrollToNextItemInGroup should only update those states when noIndex=true
    // But there are cases where scrollToNextItemInGroup() is being called after updateStates() [See handleNext() and handlePrevious()],
    // which overrides the states set by updateStates()
    if (this.noIndex) {
      this.isFirstSlide = newLeftPos === 0;
      this.isLastSlide = newLeftPos + visibleWidth >= containerWidth;
    }

    this.slideChange.emit(this.slideIndex);
  }
}
